import React, { useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { Table } from "antd";
import { IconCheck, IconForbid } from "@tabler/icons-react";
import { useCustomer } from "_contexts/useCustomer";
import { portalTradelines } from "_graphql/queries/portal";
import "./Accounts.scss";

// Mobile breakpoint in pixels (matches $breakpoint-large from variables)
const MOBILE_BREAKPOINT = 1024;

function Accounts() {
  const client = useApolloClient();
  const { applicationId } = useCustomer();
  const [loading, setLoading] = useState(true);
  const [tradelines, setTradelines] = useState([]);
  const [stats, setStats] = useState({
    selected: 0,
    total: 0,
    payment: 0,
    balance: 0,
    limit: 0,
  });
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  );

  // Listen for window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchTradelines = async () => {
    if (!applicationId) return;

    try {
      const queryData = await client.query({
        variables: { id: applicationId },
        query: portalTradelines,
      });

      const responseData = queryData?.data?.portalTradelines;

      // Sort tradelines: enrolled first, then by balance (highest first)
      const sortedTradelines = (responseData?.tradelines || []).sort((a, b) => {
        // First, sort by enrolled status (selected = true first)
        if (a.selected !== b.selected) {
          return a.selected ? -1 : 1;
        }

        // Then sort by balance (highest first)
        const balanceA = parseInt(a.tradeline?.balanceAmount || 0);
        const balanceB = parseInt(b.tradeline?.balanceAmount || 0);
        return balanceB - balanceA;
      });

      setTradelines(sortedTradelines);
      setStats({
        selected: responseData?.selected || 0,
        total: responseData?.total || 0,
        payment: responseData?.payment || 0,
        balance: responseData?.balance || 0,
        limit: responseData?.limit || 0,
      });
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTradelines();
  }, [applicationId]);

  const enrolledCount = tradelines.filter((t) => t.selected).length;
  const usagePercent =
    stats.limit > 0 ? Math.round((stats.balance / stats.limit) * 100) : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const maskAccountNumber = (accNum) => {
    if (!accNum) return "****";
    const last4 = accNum.slice(-4);
    return `****${last4}`;
  };

  const columns = [
    {
      title: "Bank / Account No.",
      key: "account",
      width: 224,
      render: (_, record) => (
        <div className="account-cell">
          <div className="account-icon">
            {record.tradeline?.customerName?.charAt(0) || "?"}
          </div>
          <div className="account-info">
            <span className="account-name">
              {record.tradeline?.customerName || "Unknown"}
            </span>
            <span className="account-number">
              {maskAccountNumber(record.tradeline?.accountNumber)}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 170,
      render: (_, record) => {
        const statusMap = {
          O: "Open",
          C: "Closed",
          P: "Paid / Zero Balance",
          U: "Unknown",
        };
        const statusCode = record.tradeline?.openOrClosed || "U";
        const statusText = statusMap[statusCode];
        const isClosed = statusCode === "C";
        return (
          <span className={`status-badge ${isClosed ? "closed" : "open"}`}>
            {statusText}
          </span>
        );
      },
    },
    {
      title: "Payment",
      key: "payment",
      width: 170,
      render: (_, record) => (
        <span className="payment-value">
          {formatCurrency(record.tradeline?.scheduledPaymentAmount || 0)}
        </span>
      ),
    },
    {
      title: "Balance",
      key: "balance",
      width: 170,
      render: (_, record) => (
        <span className="balance-value">
          {formatCurrency(record.tradeline?.balanceAmount || 0)}
        </span>
      ),
    },
    {
      title: "Credit Limit",
      key: "limit",
      width: 170,
      render: (_, record) => (
        <span className="limit-value">
          {formatCurrency(record.tradeline?.creditLimitAmount || 0)}
        </span>
      ),
    },
    {
      title: "Type",
      key: "type",
      width: 170,
      render: (_, record) => (
        <span className="type-value">
          {record.tradeline?.accountTypeText || "Unknown"}
        </span>
      ),
    },
    {
      title: "Enrolled in Program",
      key: "enrolled",
      width: 170,
      align: "center",
      render: (_, record) =>
        record.selected ? (
          <span className="enrolled-badge yes">
            <IconCheck size={12} stroke={2} />
            Yes
          </span>
        ) : (
          <span className="enrolled-badge no">
            <IconForbid size={12} />
            No
          </span>
        ),
    },
  ];

  if (loading) {
    return (
      <div className="accounts-page">
        <div className="dashboard-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  const enrolledAccounts = tradelines.filter((t) => t.selected);
  const notEnrolledAccounts = tradelines.filter((t) => !t.selected);

  // Mobile Account Item Component
  const MobileAccountItem = ({ account, isLast }) => (
    <div className={`mobile-account-item ${isLast ? "last" : ""}`}>
      <div className="mobile-account-details">
        <div className="mobile-account-icon">
          {account.tradeline?.customerName?.charAt(0) || "?"}
        </div>
        <div className="mobile-account-info">
          <span className="mobile-account-name">
            {account.tradeline?.customerName || "Unknown"}
          </span>
          <span className="mobile-account-number">
            {maskAccountNumber(account.tradeline?.accountNumber)}
          </span>
        </div>
      </div>
      <span className="mobile-account-balance">
        {formatCurrency(account.tradeline?.balanceAmount || 0)}
      </span>
    </div>
  );

  return (
    <div className="accounts-page">
      {/* Page Header */}
      <div className="page-header">
        {isMobile ? (
          <>
            <h1 className="page-title">Accounts</h1>
            <p className="page-subtitle">
              Track each enrolled account in one place. Balances, limits,
              payments & program status at a glance.
            </p>
          </>
        ) : (
          <>
            <div className="header-row">
              <h1 className="page-title">Enrolled Accounts</h1>
              <div className="enrolled-count-badge">{enrolledCount}</div>
            </div>
            <p className="page-subtitle">
              Track each enrolled account in one place. Balances, limits,
              payments & program status at a glance.
            </p>
          </>
        )}
      </div>

      {/* Combined Card: Credit Usage + Accounts */}
      <div className="accounts-card">
        {/* Credit Usage Section */}
        <div className="credit-usage-section">
          <h2 className="section-title">Credit Usage</h2>
          <div className="usage-bar-container">
            <div className="usage-bar-background">
              <div
                className="usage-bar-fill"
                style={{ width: `${usagePercent}%` }}
              />
            </div>
            {isMobile ? (
              <div className="usage-stats mobile">
                <div className="stat-item">
                  <span className="stat-label">Balance</span>
                  <span className="stat-value">
                    {formatCurrency(stats.balance)}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Limit</span>
                  <span className="stat-value">
                    {formatCurrency(stats.limit)}
                  </span>
                  <span className="usage-percentage-badge">{usagePercent}%</span>
                </div>
              </div>
            ) : (
              <div className="usage-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Balance</span>
                  <span className="stat-value">
                    {formatCurrency(stats.balance)}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Enrolled in Program</span>
                  <span className="stat-value">
                    {enrolledCount}/{stats.total}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Credit Limit</span>
                  <span className="stat-value">
                    {formatCurrency(stats.limit)}
                  </span>
                  <span className="usage-percentage-badge">{usagePercent}%</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile: Card-based accounts list */}
        {isMobile ? (
          <div className="mobile-accounts-container">
            {/* Enrolled Accounts */}
            <div className="mobile-accounts-section">
              <div className="mobile-section-header">
                <span className="mobile-section-title">Enrolled Accounts</span>
                <span className="mobile-section-badge">{enrolledAccounts.length}</span>
              </div>
              <div className="mobile-accounts-list">
                {enrolledAccounts.map((account, index) => (
                  <MobileAccountItem
                    key={account.id}
                    account={account}
                    isLast={index === enrolledAccounts.length - 1}
                  />
                ))}
              </div>
            </div>

            {/* Not Enrolled Accounts */}
            {notEnrolledAccounts.length > 0 && (
              <div className="mobile-accounts-section">
                <div className="mobile-section-header">
                  <span className="mobile-section-title">Not Enrolled</span>
                  <span className="mobile-section-badge">{notEnrolledAccounts.length}</span>
                </div>
                <div className="mobile-accounts-list">
                  {notEnrolledAccounts.map((account, index) => (
                    <MobileAccountItem
                      key={account.id}
                      account={account}
                      isLast={index === notEnrolledAccounts.length - 1}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Desktop: Table */
          <div className="accounts-table-container">
            <Table
              columns={columns}
              dataSource={tradelines}
              rowKey="id"
              pagination={false}
              showHeader={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Accounts;
